import * as React from 'react';
import type { Question, QuestionnaireSchema, Section } from '../types';
import { numberToLetterId } from '../utils';
import type { QuizQuestion } from './types';

function isSection(item: Section | Question): item is Section {
  return typeof item === 'object' && item !== null && 'type' in item && (item as Section).type === 'section';
}

function normalizeOptions(question: Question) {
  const options = (question as { options?: { id: string; label: string; value: string }[] }).options || [];
  return options.map((option, idx) => {
    const text = option.label?.trim() || (option.value != null ? String(option.value) : '') || `选项 ${idx + 1}`;
    return {
      id: option.id || numberToLetterId(idx + 1),
      text,
      value: option.value ?? option.label ?? null,
    };
  });
}

function extractCorrectIds(question: Question): string[] {
  if (question.type === 'single_choice') {
    const ca = (question as { correctAnswer?: string }).correctAnswer;
    return ca ? [ca] : [];
  }
  if (question.type === 'multiple_choice') {
    const arr = (question as { correctAnswers?: string[] }).correctAnswers;
    return arr?.filter(Boolean) || [];
  }
  return [];
}

function mapQuestion(question: Question, fallbackIndex: number, defaultScore: number): QuizQuestion | null {
  if (question.type !== 'single_choice' && question.type !== 'multiple_choice') {
    return null;
  }
  const options = normalizeOptions(question);
  if (!options.length) return null;
  const correctIds = extractCorrectIds(question)
    .map((val) => (val != null ? String(val) : ''))
    .filter(Boolean);

  return {
    id: question.id || String(fallbackIndex + 1),
    title: question.title || `第 ${fallbackIndex + 1} 题`,
    description: question.description,
    category: (question as { category?: string }).category || '常识',
    difficulty: (question as { difficulty?: string }).difficulty,
    type: question.type === 'single_choice' ? 'single' : 'multiple',
    options,
    correctIds,
    score: typeof question.score === 'number' ? question.score : defaultScore,
    explanation: question.explanation,
  };
}

export function useQuizSchema(
  schema: QuestionnaireSchema | Record<string, unknown> | null | undefined,
  options: { defaultScore?: number } = {}
): QuizQuestion[] {
  const defaultScore = options.defaultScore ?? 1;
  return React.useMemo(() => {
    if (!schema || typeof schema !== 'object') return [];
    const normalized = schema as QuestionnaireSchema;
    const sections = normalized.sections || [];
    const gathered: Question[] = [];
    sections.forEach((item) => {
      if (isSection(item as Section)) {
        gathered.push(...(((item as Section).questions || []) as Question[]));
      } else {
        gathered.push(item as Question);
      }
    });
    const out: QuizQuestion[] = [];
    gathered.forEach((question, idx) => {
      const mapped = mapQuestion(question, idx, defaultScore);
      if (mapped) out.push(mapped);
    });
    return out;
  }, [schema, defaultScore]);
}
